// src/components/features/brands/CompetitorsManager.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  TextField,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  Link,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Notes as NotesIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';

// Import custom components
import { Card } from '../../design-system';
import { EmptyState } from '../../design-system/components/core';

// Social media platforms
const SOCIAL_PLATFORMS = [
  { key: 'facebook', label: 'Facebook', icon: FacebookIcon },
  { key: 'instagram', label: 'Instagram', icon: InstagramIcon },
  { key: 'twitter', label: 'Twitter', icon: TwitterIcon },
  { key: 'linkedin', label: 'LinkedIn', icon: LinkedInIcon },
  { key: 'youtube', label: 'YouTube', icon: YouTubeIcon },
  { key: 'tiktok', label: 'TikTok', icon: LanguageIcon },
];

// Format URL with protocol if missing
const formatUrl = (url) => {
  if (!url) return '';
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

const CompetitorsManager = ({
  competitors = [],
  onUpdate,
  onAdd,
  onDelete,
  canEdit = true,
}) => {
  const theme = useTheme();
  
  // Local state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCompetitor, setCurrentCompetitor] = useState({
    name: '',
    website: '',
    socialAccounts: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: '',
      tiktok: ''
    },
    notes: ''
  });
  
  // Handle dialog open for new competitor
  const handleAddCompetitor = () => {
    setCurrentCompetitor({
      name: '',
      website: '',
      socialAccounts: {
        facebook: '',
        instagram: '',
        twitter: '',
        linkedin: '',
        youtube: '',
        tiktok: ''
      },
      notes: ''
    });
    setEditMode(false);
    setDialogOpen(true);
  };
  
  // Handle dialog open for editing
  const handleEditCompetitor = (competitor) => {
    // Ensure all social accounts fields exist
    const socialAccounts = {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: '',
      tiktok: '',
      ...competitor.socialAccounts
    };
    
    setCurrentCompetitor({ 
      ...competitor,
      socialAccounts
    });
    setEditMode(true);
    setDialogOpen(true);
  };
  
  // Handle basic input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCompetitor(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle social account input change
  const handleSocialChange = (platform, value) => {
    setCurrentCompetitor(prev => ({
      ...prev,
      socialAccounts: {
        ...prev.socialAccounts,
        [platform]: value
      }
    }));
  };
  
  // Handle form submission
  const handleSubmit = () => {
    // Validate form
    if (!currentCompetitor.name.trim()) {
      alert('Competitor name is required');
      return;
    }
    
    // Format URL if provided
    if (currentCompetitor.website) {
      currentCompetitor.website = formatUrl(currentCompetitor.website);
    }
    
    // Format social URLs
    const formattedSocialAccounts = {};
    
    for (const [platform, url] of Object.entries(currentCompetitor.socialAccounts)) {
      formattedSocialAccounts[platform] = url ? formatUrl(url) : '';
    }
    
    const competitorData = {
      ...currentCompetitor,
      socialAccounts: formattedSocialAccounts
    };
    
    if (editMode) {
      onUpdate(competitorData);
    } else {
      onAdd(competitorData);
    }
    
    setDialogOpen(false);
  };
  
  // Handle competitor deletion
  const handleDeleteCompetitor = (competitorId) => {
    if (window.confirm('Are you sure you want to delete this competitor?')) {
      onDelete(competitorId);
    }
  };
  
  // Count active social profiles
  const countSocialProfiles = (competitor) => {
    if (!competitor.socialAccounts) return 0;
    return Object.values(competitor.socialAccounts).filter(Boolean).length;
  };
  
  return (
    <Box>
      {/* Header with add button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Competitors
        </Typography>
        
        {canEdit && (
          <Button
            variant="contained"
            
            startIcon={<AddIcon />}
            onClick={handleAddCompetitor}
          >
            Add Competitor
          </Button>
        )}
      </Box>
      
      {/* Competitor List */}
      {competitors.length === 0 ? (
        <EmptyState
          title="No Competitors Added"
          description="Add your brand's competitors for benchmarking and monitoring"
          icon={<BusinessIcon sx={{ fontSize: 64 }} />}
          action={
            canEdit ? (
              <Button
                variant="contained"
                
                startIcon={<AddIcon />}
                onClick={handleAddCompetitor}
              >
                Add Competitor
              </Button>
            ) : null
          }
        />
      ) : (
        <Grid container spacing={3}>
          {competitors.map((competitor, index) => (
            <Grid item xs={12} md={6} key={competitor._id || index}>
              <Card magical={false}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mr: 2,
                      }}
                    >
                      <BusinessIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">
                        {competitor.name}
                      </Typography>
                      {competitor.website && (
                        <Link 
                          href={formatUrl(competitor.website)} 
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: '0.875rem'
                          }}
                        >
                          <LanguageIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {competitor.website}
                        </Link>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Social Accounts */}
                  {competitor.socialAccounts && countSocialProfiles(competitor) > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Social Profiles:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {SOCIAL_PLATFORMS.map(platform => {
                          const url = competitor.socialAccounts[platform.key];
                          if (!url) return null;
                          
                          const Icon = platform.icon;
                          return (
                            <IconButton
                              key={platform.key}
                              component="a"
                              href={formatUrl(url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                              sx={{
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                  color: theme.palette.primary.main,
                                }
                              }}
                              title={`${platform.label}: ${url}`}
                            >
                              <Icon />
                            </IconButton>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Notes */}
                  {competitor.notes && (
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5, 
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        display: 'flex'
                      }}
                    >
                      <NotesIcon 
                        fontSize="small" 
                        sx={{ 
                          mr: 1,
                          color: theme.palette.text.secondary,
                          mt: '2px'
                        }} 
                      />
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{
                          whiteSpace: 'pre-line',
                        }}
                      >
                        {competitor.notes}
                      </Typography>
                    </Paper>
                  )}
                </CardContent>
                
                {canEdit && (
                  <CardActions sx={{ justifyContent: 'flex-end' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditCompetitor(competitor)}
                      title="Edit competitor"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteCompetitor(competitor._id)}
                      title="Delete competitor"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Competitor' : 'Add Competitor'}
          <IconButton
            aria-label="close"
            onClick={() => setDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Competitor Name"
                name="name"
                value={currentCompetitor.name}
                onChange={handleInputChange}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Website"
                name="website"
                value={currentCompetitor.website}
                onChange={handleInputChange}
                fullWidth
                placeholder="https://example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LanguageIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Social Media Accounts */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Social Media Accounts
              </Typography>
            </Grid>
            
            {SOCIAL_PLATFORMS.map(platform => (
              <Grid item xs={12} md={6} key={platform.key}>
                <TextField
                  label={platform.label}
                  value={currentCompetitor.socialAccounts[platform.key] || ''}
                  onChange={(e) => handleSocialChange(platform.key, e.target.value)}
                  fullWidth
                  placeholder={`https://${platform.key}.com/handle`}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <platform.icon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            ))}
            
            {/* Notes */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Additional Information
              </Typography>
              
              <TextField
                label="Notes"
                name="notes"
                value={currentCompetitor.notes || ''}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Add notes about this competitor, their strengths, weaknesses, etc."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                      <NotesIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
          >
            {editMode ? 'Save Changes' : 'Add Competitor'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompetitorsManager;